/**
 * Helper utility for converting Tailwind classes to design tokens
 * This utility provides mappings and conversion functions to help developers
 * migrate from hardcoded Tailwind classes to design system tokens.
 */

// Spacing conversions
export const SPACING_MAP = {
  // Padding
  'p-0': 'SURFACE_PADDING.none',
  'p-1': 'SURFACE_PADDING["1"]',
  'p-2': 'SURFACE_PADDING["2"]',
  'p-3': 'SURFACE_PADDING["3"]',
  'p-4': 'SURFACE_PADDING["4"]',
  'p-6': 'SURFACE_PADDING["6"]',
  'p-8': 'SURFACE_PADDING["8"]',
  'px-0': 'PADDING_X["0"]',
  'px-1': 'PADDING_X["1"]',
  'px-2': 'PADDING_X["2"]',
  'px-2.5': 'PADDING_X["2.5"]',
  'px-3': 'PADDING_X["3"]',
  'px-4': 'PADDING_X["4"]',
  'px-6': 'PADDING_X["6"]',
  'px-8': 'PADDING_X["8"]',
  'py-0': 'PADDING_Y["0"]',
  'py-0.5': 'PADDING_Y["0.5"]',
  'py-1': 'PADDING_Y["1"]',
  'py-1.5': 'PADDING_Y["1.5"]',
  'py-2': 'PADDING_Y["2"]',
  'py-2.5': 'PADDING_Y["2.5"]',
  'py-3': 'PADDING_Y["3"]',
  'py-4': 'PADDING_Y["4"]',
  'py-6': 'PADDING_Y["6"]',
  'py-8': 'PADDING_Y["8"]',
  'py-12': 'PADDING_Y["12"]',
  'py-16': 'PADDING_Y["16"]',
  'pl-0': 'PADDING_LEFT["0"]',
  'pl-2': 'PADDING_LEFT["2"]',
  'pl-4': 'PADDING_LEFT["4"]',
  'pr-0': 'PADDING_RIGHT["0"]',
  'pr-2': 'PADDING_RIGHT["2"]',
  'pr-4': 'PADDING_RIGHT["4"]',
  'pt-0': 'PADDING_TOP["0"]',
  'pt-2': 'PADDING_TOP["2"]',
  'pb-0': 'PADDING_BOTTOM["0"]',
  'pb-4': 'PADDING_BOTTOM["4"]',

  // Margins
  'm-0': 'MARGIN_X["0"]',
  'm-1': 'MARGIN_X["1"]',
  'm-2': 'MARGIN_X["2"]',
  'm-3': 'MARGIN_X["3"]',
  'm-4': 'MARGIN_X["4"]',
  'mx-0': 'MARGIN_X["0"]',
  'mx-1': 'MARGIN_X["1"]',
  'mx-2': 'MARGIN_X["2"]',
  'mx-3': 'MARGIN_X["3"]',
  'mx-4': 'MARGIN_X["4"]',
  'my-0': 'MARGIN_Y["0"]',
  'my-1': 'MARGIN_Y["1"]',
  'my-2': 'MARGIN_Y["2"]',
  'my-3': 'MARGIN_Y["3"]',
  'my-4': 'MARGIN_Y["4"]',
  'mt-0': 'MARGIN_TOP["0"]',
  'mt-1': 'MARGIN_TOP["1"]',
  'mt-2': 'MARGIN_TOP["2"]',
  'mt-3': 'MARGIN_TOP["3"]',
  'mt-4': 'MARGIN_TOP["4"]',
  'mt-6': 'MARGIN_TOP["6"]',
  'mt-10': 'MARGIN_TOP["10"]',
  'mb-0': 'MARGIN_BOTTOM["0"]',
  'mb-1': 'MARGIN_BOTTOM["1"]',
  'mb-2': 'MARGIN_BOTTOM["2"]',
  'mb-3': 'MARGIN_BOTTOM["3"]',
  'mb-4': 'MARGIN_BOTTOM["4"]',
  'mb-6': 'MARGIN_BOTTOM["6"]',
  'ml-0': 'MARGIN_LEFT["0"]',
  'ml-1': 'MARGIN_LEFT["1"]',
  'ml-2': 'MARGIN_LEFT["2"]',
  'ml-auto': 'MARGIN_LEFT.auto',
  'mr-0': 'MARGIN_RIGHT["0"]',
  'mr-1': 'MARGIN_RIGHT["1"]',
  'mr-2': 'MARGIN_RIGHT["2"]',
  'mr-auto': 'MARGIN_RIGHT.auto',

  // Gaps
  'gap-0': 'GAP_SCALE.none',
  'gap-0.5': 'GAP_SCALE["0.5"]',
  'gap-1': 'GAP_SCALE["1"]',
  'gap-1.5': 'GAP_SCALE["1.5"]',
  'gap-2': 'GAP_SCALE["2"]',
  'gap-2.5': 'GAP_SCALE["2.5"]',
  'gap-3': 'GAP_SCALE["3"]',
  'gap-3.5': 'GAP_SCALE["3.5"]',
  'gap-4': 'GAP_SCALE["4"]',
  'gap-5': 'GAP_SCALE["5"]',
  'gap-6': 'GAP_SCALE["6"]',
  'gap-7': 'GAP_SCALE["7"]',
  'gap-8': 'GAP_SCALE["8"]',

  // Additional margins
  'mt-0.5': 'MARGIN_TOP["0.5"]',
  'mt-[1.5rem]': 'MARGIN_TOP["1.5rem"]',
  'mb-[2rem]': 'MARGIN_BOTTOM["2rem"]',

  // Negative margins
  '-mx-1': 'MARGIN_X.negative1',
  '-mx-4': 'MARGIN_X.negative4',
  '-my-1': 'MARGIN_Y.negative1',
  '-mt-1': 'MARGIN_TOP.negative1',
  '-mb-1': 'MARGIN_BOTTOM.negative1',
};

// Typography conversions
export const TYPOGRAPHY_MAP = {
  'text-xs': 'TYPE_SCALE.xs',
  'text-sm': 'TYPE_SCALE.sm',
  'text-base': 'TYPE_SCALE.base',
  'text-lg': 'TYPE_SCALE.lg',
  'text-xl': 'TYPE_SCALE.xl',
  'text-2xl': 'TYPE_SCALE["2xl"]',
  'text-3xl': 'TYPE_SCALE["3xl"]',
  'text-4xl': 'TYPE_SCALE["4xl"]',
  'text-5xl': 'TYPE_SCALE["5xl"]',
  'text-6xl': 'TYPE_SCALE["6xl"]',
};

// Color conversions (common hardcoded colors to design tokens)
export const COLOR_MAP = {
  // Background colors
  'bg-white': 'COLOR_SCALE.surface',
  'bg-black': 'COLOR_SCALE.foreground',
  'bg-gray-50': 'COLOR_SCALE["surface-muted"]',
  'bg-gray-100': 'COLOR_SCALE["surface-subtle"]',
  'bg-gray-200': 'COLOR_SCALE["surface-hover"]',
  'bg-gray-400': 'COLOR_SCALE["surface-hover"]',
  'bg-slate-50': 'COLOR_SCALE["surface-muted"]',
  'bg-slate-100': 'COLOR_SCALE["surface-subtle"]',
  'bg-blue-50': 'COLOR_SCALE["surface-blue"]',
  'bg-purple-100': 'COLOR_SCALE["surface-purple"]',
  'bg-green-50': 'COLOR_SCALE["surface-green"]',
  'bg-red-50': 'COLOR_SCALE["surface-red"]',
  'bg-red-500': 'COLOR_SCALE["bg-red-500"]',
  'bg-blue-500': 'COLOR_SCALE["bg-blue-500"]',
  'bg-blue-100': 'COLOR_SCALE["bg-blue-100"]',
  'bg-blue-200': 'COLOR_SCALE["bg-blue-200"]',
  'bg-green-100': 'COLOR_SCALE["bg-green-100"]',
  'bg-green-500': 'COLOR_SCALE["bg-green-500"]',
  'bg-primary': 'COLOR_SCALE.primary',
  'bg-indigo-600': 'COLOR_SCALE["indigo-600-bg"]',

  // Text colors
  'text-gray-500': 'COLOR_SCALE["foreground-muted"]',
  'text-gray-600': 'COLOR_SCALE["foreground-muted"]',
  'text-gray-700': 'COLOR_SCALE.foreground',
  'text-gray-800': 'COLOR_SCALE.foreground',
  'text-gray-900': 'COLOR_SCALE.foreground',
  'text-slate-600': 'COLOR_SCALE["foreground-muted"]',
  'text-slate-700': 'COLOR_SCALE.foreground',
  'text-slate-800': 'COLOR_SCALE.foreground',
  'text-slate-900': 'COLOR_SCALE.foreground',
  'text-blue-600': 'COLOR_SCALE["foreground-blue"]',
  'text-purple-600': 'COLOR_SCALE["foreground-purple"]',
  'text-green-600': 'COLOR_SCALE["foreground-green"]',
  'text-red-500': 'COLOR_SCALE["foreground-red"]',
  'text-red-600': 'COLOR_SCALE["foreground-red"]',
  'text-red-700': 'COLOR_SCALE["foreground-red"]',
  'text-primary': 'COLOR_SCALE.primary',
  'text-accent-foreground': 'COLOR_SCALE.accent',
  'text-transparent': 'TEXT.transparent',
  'text-muted-foreground': 'COLOR_SCALE["foreground-muted"]',
  'text-muted-foreground/50': 'COLOR_SCALE["foreground-muted"]@50',
  'text-muted-foreground/70': 'COLOR_SCALE["foreground-muted"]@70',
  'text-white': 'COLOR_SCALE.surface',
  'text-black': 'COLOR_SCALE.foreground',
  'text-indigo-600': 'COLOR_SCALE["foreground-indigo"]',
  'text-on-dark': 'COLOR_SCALE["text-on-dark"]',

  // Border colors
  'border-gray-200': 'COLOR_SCALE["border-subtle"]',
  'border-gray-300': 'COLOR_SCALE["border-muted"]',
  'border-slate-200': 'COLOR_SCALE["border-subtle"]',
  'border-slate-300': 'COLOR_SCALE["border-muted"]',
  'border-blue-200': 'COLOR_SCALE["border-blue"]',
  'border-red-200': 'COLOR_SCALE["border-red"]',
  'border-green-200': 'COLOR_SCALE["border-green"]',
  'border-purple-200': 'COLOR_SCALE["border-purple"]',
  'blue-500-border': 'COLOR_SCALE["blue-500-border"]',

  // Hover states
  'hover:bg-blue-50': 'COLOR_SCALE["surface-blue"]',
  'hover:bg-blue-200': 'BACKGROUND.hoverBlue200',
  'hover:bg-red-50': 'COLOR_SCALE["surface-red"]',
  'hover:bg-green-200': 'BACKGROUND.hoverGreen200',
  'hover:bg-gray-200': 'COLOR_SCALE["surface-hover"]',
  'hover:bg-purple-200': 'COLOR_SCALE["surface-purple"]',
  'hover:bg-muted': 'COLOR_SCALE["surface-muted"]@hover',
  'hover:border-accent/80': 'COLOR_SCALE.accent@hover80',
  'hover:border-border': 'COLOR_SCALE.border@hover',
  'hover:border-blue-300': 'BORDER.hoverBlue300',
  'hover:border-green-500': 'BORDER.hoverGreen500',
  'hover:border-blue-500': 'BORDER.hoverBlue500',
  'hover:text-red-700': 'COLOR_SCALE["foreground-red"]',
  'hover:text-blue-700': 'COLOR_SCALE["text-blue-700"]',
  'hover:text-green-700': 'COLOR_SCALE["text-green-700"]',
  'hover:text-blue-600': 'COLOR_SCALE["text-blue-600"]',
  'hover:text-blue-900': 'COLOR_SCALE["text-blue-900"]',
  'hover:text-green-900': 'COLOR_SCALE["text-green-900"]',
  'hover:text-foreground': 'COLOR_SCALE.foreground@hover',

  // Active states
  'active:text-blue-700': 'COLOR_SCALE["text-blue-700"]@active',
  'active:text-gray-800': 'COLOR_SCALE.foreground@active',
  'active:bg-gray-100': 'COLOR_SCALE["surface-hover"]@active',
  'active:border-blue-400': 'BORDER.activeBlue400',

  // Focus states
  'focus:outline-none': 'FOCUS.outlineNone',
  'focus:ring-2': 'FOCUS.ring2',
  'focus:ring-blue-500': 'FOCUS.ringBlue500',
  'focus:ring-opacity-50': 'FOCUS.ringOpacity50',

  // Design system semantic colors (from allowedColors array)
  'text-foreground': 'COLOR_SCALE.foreground',
  'text-background': 'COLOR_SCALE.surface',
  'text-muted': 'COLOR_SCALE["foreground-muted"]',
  'text-accent': 'COLOR_SCALE.accent',
  'text-secondary': 'COLOR_SCALE.secondary',
  'text-destructive': 'COLOR_SCALE.destructive',
  'text-card': 'COLOR_SCALE.card',
  'text-popover': 'COLOR_SCALE.popover',
  'text-input': 'COLOR_SCALE.input',
  'text-ring': 'COLOR_SCALE.ring',
  'text-border': 'COLOR_SCALE.border',
  'text-surface': 'COLOR_SCALE.surface',
  'text-surface-muted': 'COLOR_SCALE["surface-muted"]',
  'text-surface-subtle': 'COLOR_SCALE["surface-subtle"]',
  'text-foreground-muted': 'COLOR_SCALE["foreground-muted"]',
  'text-foreground-subtle': 'COLOR_SCALE["foreground-subtle"]',
  'text-icon-muted': 'COLOR_SCALE["icon-muted"]',
  'text-success': 'COLOR_SCALE.success',
  'text-warning': 'COLOR_SCALE.warning',
  'text-info': 'COLOR_SCALE.info',
  'text-error': 'COLOR_SCALE.error',
  'text-interactive-primary': 'COLOR_SCALE["interactive-primary"]',
  'text-interactive-primary-hover': 'COLOR_SCALE["interactive-primary-hover"]',
  'text-status-success': 'COLOR_SCALE["status-success"]',
  'text-status-success-bg': 'COLOR_SCALE["status-success-bg"]',
  'text-status-success-border': 'COLOR_SCALE["status-success-border"]',
  'text-status-error': 'COLOR_SCALE["status-error"]',
  'text-status-error-hover': 'COLOR_SCALE["status-error-hover"]',
  'text-status-warning': 'COLOR_SCALE["status-warning"]',
  'text-status-warning-bg': 'COLOR_SCALE["status-warning-bg"]',
  'text-status-warning-border': 'COLOR_SCALE["status-warning-border"]',
  'text-text-strong': 'COLOR_SCALE["text-strong"]',
  'text-text-muted': 'COLOR_SCALE["text-muted"]',
  'text-icon-purple': 'COLOR_SCALE["icon-purple"]',
  'text-icon-blue': 'COLOR_SCALE["icon-blue"]',
  'text-icon-green': 'COLOR_SCALE["icon-green"]',
  'text-icon-red': 'COLOR_SCALE["icon-red"]',

  'bg-foreground': 'COLOR_SCALE.foreground',
  'bg-background': 'COLOR_SCALE.surface',
  'bg-muted': 'COLOR_SCALE["surface-muted"]',
  'bg-accent': 'COLOR_SCALE.accent',
  'bg-secondary': 'COLOR_SCALE.secondary',
  'bg-destructive': 'COLOR_SCALE.destructive',
  'bg-card': 'COLOR_SCALE.card',
  'bg-popover': 'COLOR_SCALE.popover',
  'bg-input': 'COLOR_SCALE.input',
  'bg-ring': 'COLOR_SCALE.ring',
  'bg-border': 'COLOR_SCALE.border',
  'bg-clip-text': 'BACKGROUND.clipText',
  'bg-surface': 'COLOR_SCALE.surface',
  'bg-surface-muted': 'COLOR_SCALE["surface-muted"]',
  'bg-surface-subtle': 'COLOR_SCALE["surface-subtle"]',
  'bg-foreground-muted': 'COLOR_SCALE["foreground-muted"]',
  'bg-foreground-subtle': 'COLOR_SCALE["foreground-subtle"]',
  'bg-icon-muted': 'COLOR_SCALE["icon-muted"]',
  'bg-success': 'COLOR_SCALE.success',
  'bg-warning': 'COLOR_SCALE.warning',
  'bg-info': 'COLOR_SCALE.info',
  'bg-error': 'COLOR_SCALE.error',
  'bg-interactive-primary': 'COLOR_SCALE["interactive-primary"]',
  'bg-interactive-primary-hover': 'COLOR_SCALE["interactive-primary-hover"]',
  'bg-status-success': 'COLOR_SCALE["status-success"]',
  'bg-status-success-bg': 'COLOR_SCALE["status-success-bg"]',
  'bg-status-success-border': 'COLOR_SCALE["status-success-border"]',
  'bg-status-error': 'COLOR_SCALE["status-error"]',
  'bg-status-error-hover': 'COLOR_SCALE["status-error-hover"]',
  'bg-status-warning': 'COLOR_SCALE["status-warning"]',
  'bg-status-warning-bg': 'COLOR_SCALE["status-warning-bg"]',
  'bg-status-warning-border': 'COLOR_SCALE["status-warning-border"]',
  'bg-text-strong': 'COLOR_SCALE["text-strong"]',
  'bg-text-muted': 'COLOR_SCALE["text-muted"]',
  'bg-icon-purple': 'COLOR_SCALE["icon-purple"]',
  'bg-icon-blue': 'COLOR_SCALE["icon-blue"]',
  'bg-icon-green': 'COLOR_SCALE["icon-green"]',
  'bg-icon-red': 'COLOR_SCALE["icon-red"]',

  'border-foreground': 'COLOR_SCALE.foreground',
  'border-background': 'COLOR_SCALE.surface',
  'border-muted': 'COLOR_SCALE["border-muted"]',
  'border-accent': 'COLOR_SCALE.accent',
  'border-secondary': 'COLOR_SCALE.secondary',
  'border-destructive': 'COLOR_SCALE.destructive',
  'border-card': 'COLOR_SCALE.card',
  'border-popover': 'COLOR_SCALE.popover',
  'border-input': 'COLOR_SCALE.input',
  'border-ring': 'COLOR_SCALE.ring',
  'border-border': 'COLOR_SCALE.border',
  'border-surface': 'COLOR_SCALE.surface',
  'border-surface-muted': 'COLOR_SCALE["surface-muted"]',
  'border-surface-subtle': 'COLOR_SCALE["surface-subtle"]',
  'border-foreground-muted': 'COLOR_SCALE["foreground-muted"]',
  'border-foreground-subtle': 'COLOR_SCALE["foreground-subtle"]',
  'border-icon-muted': 'COLOR_SCALE["icon-muted"]',
  'border-success': 'COLOR_SCALE.success',
  'border-warning': 'COLOR_SCALE.warning',
  'border-info': 'COLOR_SCALE.info',
  'border-error': 'COLOR_SCALE.error',
  'border-interactive-primary': 'COLOR_SCALE["interactive-primary"]',
  'border-interactive-primary-hover': 'COLOR_SCALE["interactive-primary-hover"]',
  'border-status-success': 'COLOR_SCALE["status-success"]',
  'border-status-success-bg': 'COLOR_SCALE["status-success-bg"]',
  'border-status-success-border': 'COLOR_SCALE["status-success-border"]',
  'border-status-error': 'COLOR_SCALE["status-error"]',
  'border-status-error-hover': 'COLOR_SCALE["status-error-hover"]',
  'border-status-warning': 'COLOR_SCALE["status-warning"]',
  'border-status-warning-bg': 'COLOR_SCALE["status-warning-bg"]',
  'border-status-warning-border': 'COLOR_SCALE["status-warning-border"]',
  'border-text-strong': 'COLOR_SCALE["text-strong"]',
  'border-text-muted': 'COLOR_SCALE["text-muted"]',
  'border-icon-purple': 'COLOR_SCALE["icon-purple"]',
  'border-icon-blue': 'COLOR_SCALE["icon-blue"]',
  'border-icon-green': 'COLOR_SCALE["icon-green"]',
  'border-icon-red': 'COLOR_SCALE["icon-red"]',
  'border-border-subtle': 'COLOR_SCALE["border-subtle"]',
  'border-border-muted': 'COLOR_SCALE["border-muted"]',

  // Bare color names (for design token definitions - these should not be used directly in CSS)
  foreground: 'COLOR_SCALE.foreground',
  background: 'COLOR_SCALE.surface',
  muted: 'COLOR_SCALE["foreground-muted"]',
  accent: 'COLOR_SCALE.accent',
  primary: 'COLOR_SCALE.primary',
  secondary: 'COLOR_SCALE.secondary',
  destructive: 'COLOR_SCALE.destructive',
  card: 'COLOR_SCALE.card',
  popover: 'COLOR_SCALE.popover',
  input: 'COLOR_SCALE.input',
  ring: 'COLOR_SCALE.ring',
  border: 'COLOR_SCALE.border',
  surface: 'COLOR_SCALE.surface',
  'surface-muted': 'COLOR_SCALE["surface-muted"]',
  'surface-subtle': 'COLOR_SCALE["surface-subtle"]',
  'foreground-muted': 'COLOR_SCALE["foreground-muted"]',
  'foreground-subtle': 'COLOR_SCALE["foreground-subtle"]',
  'icon-muted': 'COLOR_SCALE["icon-muted"]',
  success: 'COLOR_SCALE.success',
  warning: 'COLOR_SCALE.warning',
  info: 'COLOR_SCALE.info',
  error: 'COLOR_SCALE.error',
  'interactive-primary': 'COLOR_SCALE["interactive-primary"]',
  'interactive-primary-hover': 'COLOR_SCALE["interactive-primary-hover"]',
  'status-success': 'COLOR_SCALE["status-success"]',
  'status-success-bg': 'COLOR_SCALE["status-success-bg"]',
  'status-success-border': 'COLOR_SCALE["status-success-border"]',
  'status-error': 'COLOR_SCALE["status-error"]',
  'status-error-hover': 'COLOR_SCALE["status-error-hover"]',
  'status-warning': 'COLOR_SCALE["status-warning"]',
  'status-warning-bg': 'COLOR_SCALE["status-warning-bg"]',
  'status-warning-border': 'COLOR_SCALE["status-warning-border"]',
  'gray-50': 'COLOR_SCALE["gray-50"]',
  'gray-100': 'COLOR_SCALE["gray-100"]',
  'gray-200': 'COLOR_SCALE["gray-200"]',
  'gray-300': 'COLOR_SCALE["gray-300"]',
  'gray-500': 'COLOR_SCALE["gray-500"]',
  'gray-600': 'COLOR_SCALE["gray-600"]',
  'gray-700': 'COLOR_SCALE["gray-700"]',
  'gray-800': 'COLOR_SCALE["gray-800"]',
  'gray-900': 'COLOR_SCALE["gray-900"]',
  'gray-400': 'COLOR_SCALE["gray-400"]',
  'border-subtle': 'COLOR_SCALE["border-subtle"]',
  'text-strong': 'COLOR_SCALE["text-strong"]',
  'icon-purple': 'COLOR_SCALE["icon-purple"]',
  'icon-blue': 'COLOR_SCALE["icon-blue"]',
  'icon-green': 'COLOR_SCALE["icon-green"]',
  'icon-red': 'COLOR_SCALE["icon-red"]',
};

// Size conversions
export const SIZE_MAP = {
  'w-0': 'SIZE_SCALE.w0',
  'w-1': 'SIZE_SCALE.w1',
  'w-2': 'SIZE_SCALE.w2',
  'w-3': 'SIZE_SCALE.w3',
  'w-3.5': 'SIZE_SCALE.w3_5',
  'w-4': 'SIZE_SCALE.w4',
  'w-5': 'SIZE_SCALE.w5',
  'w-6': 'SIZE_SCALE.w6',
  'w-7': 'SIZE_SCALE.w7',
  'w-8': 'SIZE_SCALE.w8',
  'w-10': 'SIZE_SCALE.w10',
  'w-12': 'SIZE_SCALE.w12',
  'w-16': 'SIZE_SCALE.w16',
  'w-20': 'SIZE_SCALE.w20',
  'w-24': 'SIZE_SCALE.w24',
  'w-32': 'SIZE_SCALE.w32',
  'w-40': 'SIZE_SCALE.w40',
  'w-48': 'SIZE_SCALE.w48',
  'w-64': 'SIZE_SCALE.w64',
  'w-80': 'SIZE_SCALE.w80',
  'w-full': 'SIZE_SCALE.full',
  'w-auto': 'SIZE_SCALE.auto',

  'h-0': 'SIZE_SCALE["0"]',
  'h-full': 'SIZE_SCALE.full',
  'h-1': 'SIZE_SCALE["1"]',
  'h-2': 'SIZE_SCALE["2"]',
  'h-3': 'SIZE_SCALE["3"]',
  'h-3.5': 'SIZE_SCALE["3.5"]',
  'h-4': 'SIZE_SCALE["4"]',
  'h-6': 'SIZE_SCALE["6"]',
  'h-7': 'SIZE_SCALE["7"]',
  'h-8': 'SIZE_SCALE["8"]',
  'h-10': 'SIZE_SCALE["10"]',
  'h-12': 'SIZE_SCALE["12"]',
  'h-16': 'SIZE_SCALE["16"]',
  'h-20': 'SIZE_SCALE["20"]',
  'h-24': 'SIZE_SCALE["24"]',
  'h-32': 'SIZE_SCALE["32"]',
  'h-40': 'SIZE_SCALE["40"]',
  'h-48': 'SIZE_SCALE["48"]',
  'h-64': 'SIZE_SCALE["64"]',

  // Size utilities (width + height)
  'size-4': 'SIZE_SCALE.w4 SIZE_SCALE["4"]',
  'size-5': 'SIZE_SCALE.w5 SIZE_SCALE["5"]',
  'size-[18px]': 'SIZE_SCALE.w18 SIZE_SCALE["18"]', // Using closest available size

  // Max width
  'max-w-sm': 'SIZE_SCALE.maxWsm',
  'max-w-md': 'SIZE_SCALE.maxWmd',
  'max-w-xl': 'SIZE_SCALE.maxWxl',
  'max-w-2xl': 'SIZE_SCALE.maxW2xl',
  'max-w-7xl': 'SIZE_SCALE.maxW7xl',

  // MAX_WIDTH_SCALE references (from template literals)
  '${MAX_WIDTH_SCALE.sm}': 'SIZE_SCALE.maxWsm',

  // Min width
  'min-w-0': 'SIZE_SCALE.minW0',
  'min-w-full': 'SIZE_SCALE.minWfull',
  'min-w-[var(--component-width-xl)]': 'SIZE_SCALE.componentWidthXl',

  // Min height
  'min-h-0': 'SIZE_SCALE.minH0',
  'min-h-screen': 'SIZE_SCALE.minHScreen',

  // Max height
  'max-h-60': 'SIZE_SCALE.maxH60',
  'max-h-96': 'SIZE_SCALE.maxH96',
  'max-h-full': 'SIZE_SCALE.maxHfull',
  'max-h-[80vh]': 'SIZE_SCALE.maxH80vh',
  'max-h-[85vh]': 'SIZE_SCALE.maxH85vh',
  'max-h-[90vh]': 'SIZE_SCALE.maxH90vh',

  // Arbitrary padding
  'p-12': 'SURFACE_PADDING["12"]',

  // Arbitrary width
  'w-[14px]': 'SIZE_SCALE.w14px',
  'w-[calc(100vw-2rem)]': 'SIZE_SCALE.calc100vwMinus2rem',
  'w-[var(--component-width-sm)]': 'SIZE_SCALE.componentWidthSm',
  'w-[var(--component-width-xl)]': 'SIZE_SCALE.componentWidthXl',

  // Arbitrary height
  'h-[14px]': 'SIZE_SCALE.h14px',
  'min-h-[var(--component-height-2xl)]': 'SIZE_SCALE.componentHeight2xl',
  'min-h-[var(--component-height-4xl,120px)]': 'SIZE_SCALE.componentHeight4xl',
  'h-svh': 'SIZE_SCALE.hSvh',
  'min-h-svh': 'SIZE_SCALE.minHSvh',

  // CSS custom properties
  'w-[--sidebar-width]': 'SIZE_SCALE.sidebarWidth',
  'bg-sidebar': 'COLOR_SCALE.sidebar',
  'bg-sidebar-border': 'COLOR_SCALE.sidebarBorder',
  'border-sidebar-border': 'COLOR_SCALE.sidebarBorder',
  'bg-transparent': 'COLOR_SCALE.transparent',

  // Transition properties
  'transition-[width]': 'TRANSITION.width',
  'ease-linear': 'TRANSITION.easeLinear',
};

// Shadow conversions
export const SHADOW_MAP = {
  'shadow-sm': 'SHADOW_SCALE.sm',
  shadow: 'SHADOW_SCALE.md',
  'shadow-md': 'SHADOW_SCALE.md',
  'shadow-lg': 'SHADOW_SCALE.lg',
  'shadow-xl': 'SHADOW_SCALE.xl',
  'shadow-2xl': 'SHADOW_SCALE["2xl"]',
  'shadow-none': 'SHADOW_SCALE.none',
  'shadow-inner': 'SHADOW_SCALE.inner',
};

// Border radius conversions
export const RADIUS_MAP = {
  'rounded-none': 'RADIUS_SCALE.none',
  'rounded-sm': 'RADIUS_SCALE.sm',
  rounded: 'RADIUS_SCALE.md',
  'rounded-md': 'RADIUS_SCALE.md',
  'rounded-lg': 'RADIUS_SCALE.lg',
  'rounded-t-lg': 'RADIUS_SCALE.lgTop',
  'rounded-xl': 'RADIUS_SCALE.xl',
  'rounded-2xl': 'RADIUS_SCALE["2xl"]',
  'rounded-3xl': 'RADIUS_SCALE["3xl"]',
  'rounded-full': 'RADIUS_SCALE.full',
};

// Layout and display conversions
export const LAYOUT_MAP = {
  // Position
  relative: 'POSITION.relative',
  absolute: 'POSITION.absolute',
  fixed: 'POSITION.fixed',
  static: 'POSITION.static',
  sticky: 'POSITION.sticky',

  // Display
  flex: 'DISPLAY.flex',
  block: 'DISPLAY.block',
  inline: 'DISPLAY.inline',
  'inline-block': 'DISPLAY.inlineBlock',
  'inline-flex': 'DISPLAY.inlineFlex',
  hidden: 'DISPLAY.none',

  // Flexbox
  'flex-1': 'FLEX.flex1',
  'flex-shrink-0': 'FLEX.shrink0',
  'shrink-0': 'FLEX.shrink0',
  'flex-grow': 'FLEX.grow',
  'flex-col': 'FLEX.directionColumn',

  // Flex alignment
  'items-center': 'FLEX.alignItemsCenter',
  'items-start': 'FLEX.alignItemsStart',
  'items-end': 'FLEX.alignItemsEnd',
  'justify-center': 'FLEX.justifyCenter',
  'justify-between': 'FLEX.justifyBetween',
  'justify-end': 'FLEX.justifyEnd',
  'justify-start': 'FLEX.justifyStart',

  // Overflow
  'overflow-hidden': 'OVERFLOW.hidden',
  'overflow-x-auto': 'OVERFLOW.xAuto',
  'overflow-y-auto': 'OVERFLOW.yAuto',

  // Positioning
  'inset-0': 'POSITION.inset0',
  'inset-x-6': 'POSITION.insetX6',
  'top-0': 'POSITION.top0',
  'top-1': 'POSITION.top1',
  'top-2': 'POSITION.top2',
  'top-3': 'POSITION.top3',
  'top-4': 'POSITION.top4',
  'left-0': 'POSITION.left0',
  'left-1': 'POSITION.left1',
  'left-2': 'POSITION.left2',
  'left-3': 'POSITION.left3',
  'right-0': 'POSITION.right0',
  'right-1': 'POSITION.right1',
  'right-2': 'POSITION.right2',
  'right-3': 'POSITION.right3',
  'right-4': 'POSITION.right4',
  'bottom-1': 'POSITION.bottom1',
  'bottom-2': 'POSITION.bottom2',
  'bottom-3': 'POSITION.bottom3',

  // Z-index
  'z-0': 'Z_INDEX.base',
  'z-10': 'Z_INDEX.controls',
  'z-30': 'Z_INDEX.30',
  'z-40': 'Z_INDEX.40',
  'z-[100]': 'Z_INDEX.100',
  'z-[1000]': 'Z_INDEX.1000',

  // Pointer events
  'pointer-events-none': 'INTERACTION.pointerNone',
  'pointer-events-auto': 'INTERACTION.pointerAuto',

  // Cursor
  'cursor-pointer': 'INTERACTION.cursorPointer',
};

// Text and typography conversions
export const TEXT_MAP = {
  'text-center': 'TEXT.alignCenter',
  'text-left': 'TEXT.alignLeft',
  'text-right': 'TEXT.alignRight',
  underline: 'TEXT_DECORATION.underline',
  truncate: 'TEXT.truncate',
  'whitespace-nowrap': 'TEXT.whitespaceNoWrap',
  capitalize: 'TEXT.capitalize',
  uppercase: 'TEXT.uppercase',
  'tracking-wide': 'TEXT.trackingWide',
};

// Border conversions
export const BORDER_MAP = {
  border: 'BORDER.default',
  'border-0': 'BORDER.none',
  'border-2': 'BORDER.width2',
  'border-b': 'BORDER_BOTTOM.default',
  'border-b-2': 'BORDER_BOTTOM.2',
  'border-dashed': 'BORDER.styleDashed',
  'border-solid': 'BORDER.styleSolid',
};

// Animation and transition conversions
export const ANIMATION_MAP = {
  'animate-pulse': 'ANIMATION.pulse',
  'animate-spin': 'ANIMATION.spin',
  'transition-all': 'TRANSITION.all',
  'duration-300': 'TRANSITION.duration300',
};

// Grid and responsive conversions
export const GRID_MAP = {
  grid: 'DISPLAY.grid',
  'grid-cols-1': 'GRID.cols1',
  'grid-cols-2': 'GRID.cols2',
  'grid-cols-3': 'GRID.cols3',
  'col-span-full': 'GRID.colSpanFull',
  'col-span-1': 'GRID.colSpan1',
  'col-span-3': 'GRID.colSpan3',
  'space-y-0': 'SPACING.spaceY0',
  'space-y-0.5': 'SPACING.spaceY0_5',
  'space-y-1': 'SPACING.spaceY1',
  'space-y-1.5': 'SPACING.spaceY1_5',
  'space-y-2': 'SPACING.spaceY2',
  'space-y-3': 'SPACING.spaceY3',
  'space-y-4': 'SPACING.spaceY4',
  'space-y-6': 'SPACING.spaceY6',
  'space-x-2': 'SPACING.spaceX2',
  'divide-y': 'DIVIDE.y',
  'divide-y-0': 'DIVIDE.yNone',
  'divide-gray-100': 'DIVIDE.colorGray100',
};

// Responsive breakpoint utilities
export const RESPONSIVE_MAP = {
  'sm:hidden': 'DISPLAY.none@sm',
  'sm:inline': 'DISPLAY.inline@sm',
  'sm:block': 'DISPLAY.block@sm',
  'sm:flex': 'DISPLAY.flex@sm',
  'sm:flex-row': 'FLEX.directionRow@sm',
  'sm:flex-col': 'FLEX.directionColumn@sm',
  'sm:items-start': 'FLEX.alignItemsStart@sm',
  'sm:items-center': 'FLEX.alignItemsCenter@sm',
  'sm:justify-start': 'FLEX.justifyStart@sm',
  'sm:justify-end': 'FLEX.justifyEnd@sm',
  'sm:justify-between': 'FLEX.justifyBetween@sm',
  'sm:gap-3': 'GAP_SCALE["3"]@sm',
  'sm:gap-4': 'GAP_SCALE["4"]@sm',
  'sm:gap-8': 'GAP_SCALE["8"]@sm',
  'sm:p-2': 'SURFACE_PADDING["2"]@sm',
  'sm:p-6': 'SURFACE_PADDING["6"]@sm',
  'sm:px-2': 'PADDING_X["2"]@sm',
  'sm:px-4': 'PADDING_X["4"]@sm',
  'sm:px-6': 'PADDING_X["6"]@sm',
  'sm:py-0.5': 'PADDING_Y["0.5"]@sm',
  'sm:py-1': 'PADDING_Y["1"]@sm',
  'sm:py-6': 'PADDING_Y["6"]@sm',
  'sm:py-16': 'PADDING_Y["16"]@sm',
  'sm:text-xs': 'TYPE_SCALE.xs@sm',
  'sm:text-sm': 'TYPE_SCALE.sm@sm',
  'sm:text-lg': 'TYPE_SCALE.lg@sm',
  'sm:w-auto': 'SIZE_SCALE.auto@sm',
  'sm:w-full': 'SIZE_SCALE.full@sm',
  'sm:flex-1': 'FLEX.flex1@sm',
  'sm:static': 'POSITION.static@sm',
  'sm:whitespace-nowrap': 'TEXT.whitespaceNoWrap@sm',
  'sm:pr-2': 'PADDING_RIGHT["2"]@sm',
  'sm:pr-4': 'PADDING_RIGHT["4"]@sm',
  'sm:text-left': 'TEXT.alignLeft@sm',
  'sm:text-center': 'TEXT.alignCenter@sm',
  'sm:gap-1.5': 'GAP_SCALE["1.5"]@sm',
  'sm:gap-2': 'GAP_SCALE["2"]@sm',
  'sm:gap-5': 'GAP_SCALE["5"]@sm',
  'sm:max-w-md': 'SIZE_SCALE.maxWmd@sm',
  'sm:top-3': 'POSITION.top3@sm',
  'sm:left-3': 'POSITION.left3@sm',
  'sm:right-3': 'POSITION.right3@sm',
  'sm:w-5': 'SIZE_SCALE.w5@sm',
  'sm:w-8': 'SIZE_SCALE.w8@sm',
  'sm:w-12': 'SIZE_SCALE.w12@sm',
  'sm:w-24': 'SIZE_SCALE.w24@sm',
  'sm:h-5': 'SIZE_SCALE["5"]@sm',
  'sm:h-8': 'SIZE_SCALE["8"]@sm',
  'sm:h-12': 'SIZE_SCALE["12"]@sm',
  'sm:h-24': 'SIZE_SCALE["24"]@sm',
  'sm:space-y-6': 'SPACING.spaceY6@sm',
  'sm:mt-0': 'MARGIN_TOP["0"]@sm',
  'sm:mt-3': 'MARGIN_TOP["3"]@sm',
  'sm:mb-0': 'MARGIN_BOTTOM["0"]@sm',
  'sm:mb-3': 'MARGIN_BOTTOM["3"]@sm',
  'sm:space-y-0.5': 'SPACING.spaceY0_5@sm',
  'sm:space-y-1.5': 'SPACING.spaceY1_5@sm',

  // Pseudo-class modifiers
  'last:mb-0': 'MARGIN_BOTTOM.0@last',

  'md:hidden': 'DISPLAY.none@md',
  'md:block': 'DISPLAY.block@md',
  'md:flex': 'DISPLAY.flex@md',
  'md:grid-cols-2': 'GRID.cols2@md',
  'md:border': 'BORDER.default@md',
  'md:shadow-sm': 'SHADOW_SCALE.sm@md',
  'md:col-span-1': 'GRID.colSpan1@md',

  'lg:hidden': 'DISPLAY.none@lg',
  'lg:block': 'DISPLAY.block@lg',
  'lg:py-16': 'PADDING_Y["16"]@lg',
  'lg:col-span-1': 'GRID.colSpan1@lg',
  'lg:col-span-3': 'GRID.colSpan3@lg',
  'lg:grid-cols-3': 'GRID.cols3@lg',
  'lg:inline': 'DISPLAY.inline@lg',

  'max-sm:-mx-4': 'MARGIN_X.negative4@maxSm',
};

// Miscellaneous utilities
export const UTILITY_MAP = {
  group: 'DISPLAY.group',
  relative: 'POSITION.relative',
  'cursor-default': 'INTERACTION.cursorDefault',
  peer: 'peer', // Tailwind peer modifier
  'sr-only':
    'position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;',
  'scroll-smooth': 'scroll-behavior: smooth',
  'touch-pan-y': 'touch-action: pan-y',
  'overscroll-contain': 'overscroll-behavior: contain',
  'z-dropdown': 'z-index: 1000', // Using a standard z-index value
  'skip-to-main': 'sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2',
  'text-sidebar-foreground': 'text-sidebar-foreground', // CSS variable
  'shimmer-effect': 'shimmer-effect', // Custom shimmer animation class
  'section-smooth': 'section-smooth', // Custom section smooth class

  // Font weights
  'font-medium': 'FONT_WEIGHT.medium',
  'font-semibold': 'FONT_WEIGHT.semibold',
  'font-mono': 'FONT_WEIGHT.mono',

  // Object fit
  'object-cover': 'OBJECT_FIT.cover',

  // Opacity
  'opacity-0': 'OPACITY.0',
  'opacity-20': 'OPACITY.20',
  'opacity-60': 'OPACITY.60',
  'opacity-95': 'OPACITY.95',

  // Align
  'align-top': 'VERTICAL_ALIGN.top',

  // Focus states
  'focus:ring-green-500': 'FOCUS_RING.green',
  'focus:not-sr-only': 'DISPLAY.visible@focus',
  'focus:absolute': 'POSITION.absolute@focus',
  'focus:top-2': 'POSITION.top2@focus',
  'focus:left-2': 'POSITION.left2@focus',

  // Background gradients
  'bg-gradient-to-r': 'BACKGROUND_GRADIENT.toRight',
  'bg-gradient-to-br': 'BACKGROUND_GRADIENT.toBottomRight',
  'from-white/80': 'BACKGROUND_GRADIENT.fromWhite80',
  'via-white/60': 'BACKGROUND_GRADIENT.viaWhite60',
  'to-white/30': 'BACKGROUND_GRADIENT.toWhite30',
  'from-transparent': 'BACKGROUND_GRADIENT.fromTransparent',
  'via-white/70': 'BACKGROUND_GRADIENT.viaWhite70',
  'to-transparent': 'BACKGROUND_GRADIENT.toTransparent',
  'from-gray-900': 'BACKGROUND_GRADIENT.fromGray900',
  'via-gray-800': 'BACKGROUND_GRADIENT.viaGray800',
  'to-gray-900': 'BACKGROUND_GRADIENT.toGray900',
  'via-blue-200': 'BACKGROUND_GRADIENT.viaBlue200',
  'from-blue-50': 'BACKGROUND_GRADIENT.fromBlue50',
  'from-yellow-50': 'BACKGROUND_GRADIENT.fromYellow50',
  'from-green-50': 'BACKGROUND_GRADIENT.fromGreen50',
  'to-purple-50': 'BACKGROUND_GRADIENT.toPurple50',
  'to-amber-50': 'BACKGROUND_GRADIENT.toAmber50',
  'to-emerald-50': 'BACKGROUND_GRADIENT.toEmerald50',
  'to-indigo-50': 'BACKGROUND_GRADIENT.toIndigo50',
  'text-yellow-700': 'COLOR_SCALE["text-yellow-700"]',
  'text-green-700': 'COLOR_SCALE["text-green-700"]',
  'text-blue-700': 'COLOR_SCALE["text-blue-700"]',
  'border-yellow-200/50': 'BORDER.yellow200Half',
  'border-green-200/50': 'BORDER.green200Half',
  'border-blue-200/50': 'BORDER.blue200Half',

  // Border bottom
  'border-b': 'BORDER_BOTTOM.default',

  // Flex wrap
  'flex-wrap': 'FLEX.wrap',

  // Transitions
  'transition-colors': 'TRANSITION.colors',
  'transition-all': 'TRANSITION.all',
  'transition-transform': 'TRANSITION.transform',
  'transition-opacity': 'TRANSITION.opacity',
  'duration-200': 'TRANSITION.duration200',
  'duration-300': 'TRANSITION.duration300',

  // Group hover
  'group-hover:bg-purple-200': 'COLOR_SCALE["surface-purple"]@groupHover',
  'group-hover:bg-gray-200': 'COLOR_SCALE["surface-hover"]@groupHover',
  'group-hover:opacity-100': 'OPACITY.100@groupHover',
  'group-hover:scale-105': 'TRANSFORM.scale105@groupHover',
  'group-hover:scale-110': 'TRANSFORM.scale110@groupHover',

  // Additional padding
  'p-0.5': 'SURFACE_PADDING["0.5"]',

  // Min height
  'min-h-0': 'SIZE_SCALE.minH0',

  // Custom z-index utilities
  'z-header': 'Z_INDEX.header',
  'z-fab': 'Z_INDEX.fab',
  'z-popover': 'Z_INDEX.popover',
  'z-tooltip': 'Z_INDEX.tooltip',
  'z-overlay': 'Z_INDEX.overlay',
  'z-modal': 'Z_INDEX.modal',
  'z-toast': 'Z_INDEX.toast',

  // Custom animations
  'animate-float': 'ANIMATION.float',
  'animate-scale-in': 'ANIMATION.scaleIn',

  // Custom effects
  'btn-ripple': 'EFFECTS.btnRipple',
  'shadow-elevation-sm': 'SHADOW_SCALE.elevationSm',
  'tooltip-text': 'EFFECTS.tooltipText',

  // Dark mode class (CSS selector, not className)
  dark: 'THEME.dark',

  // Print-related utility classes
  print: 'PRINT.mediaQuery',
  'print-keep': 'PRINT.keep',
  'print-only': 'PRINT.only',
  'no-print': 'PRINT.none',
  'line-clamp-1': 'TEXT.lineClamp1',

  // Blog editor utility classes
  'blog-editor': 'BLOG_EDITOR.container',
  'ql-toolbar': 'BLOG_EDITOR.toolbar',

  // Custom UI effect classes from index.css
  'shadow-elevation-md': 'SHADOW_SCALE.elevationMd',
  'shadow-elevation-lg': 'SHADOW_SCALE.elevationLg',
  'section-accent': 'SECTION.accent',
  'hero-pattern-section': 'HERO_PATTERN.section',
  'hero-pattern-bg': 'HERO_PATTERN.background',
  'provider-dot-ring': 'PROVIDER_DOT.ring',
  'image-zoom-hover': 'IMAGE.zoomHover',
  'card-tilt': 'CARD.tilt',
  'icon-bounce-hover': 'ICON.bounceHover',
  'icon-rotate-hover': 'ICON.rotateHover',
  'icon-pulse-hover': 'ICON.pulseHover',
  'btn-magnetic': 'BUTTON.magnetic',
  'badge-hover-lift': 'BADGE.hoverLift',
  'btn-spring': 'BUTTON.spring',
  'glow-hover': 'EFFECTS.glowHover',
  'scale-pulse': 'ANIMATION.scalePulse',
  'slide-in-right': 'ANIMATION.slideInRight',
  'slide-in-left': 'ANIMATION.slideInLeft',
  'animate-slide-up': 'ANIMATION.slideUp',
  'text-reveal': 'ANIMATION.textReveal',
  'text-reveal-fast': 'ANIMATION.textRevealFast',
  'number-roll': 'ANIMATION.numberRoll',
  'wiggle-hover': 'ANIMATION.wiggleHover',
  'border-expand': 'BORDER.expand',
  'backdrop-blur-fade': 'BACKDROP.blurFade',
  'gradient-shift': 'GRADIENT.shift',
  'card-hover-enhanced': 'CARD.hoverEnhanced',
  'focus-enhanced': 'FOCUS.enhanced',
  'section-entrance': 'SECTION.entrance',
  'scroll-hint-mobile': 'SCROLL.hintMobile',
  'kbd-shine': 'KBD.shine',
  'skeleton-enhanced': 'SKELETON.enhanced',
  'btn-ripple-enhanced': 'BUTTON.rippleEnhanced',
  'tooltip-enhanced': 'TOOLTIP.enhanced',
  'gradient-border-animated': 'BORDER.gradientAnimated',
  'scroll-progress-enhanced': 'SCROLL.progressEnhanced',
  'glass-panel': 'GLASS.panel',
  'glass-card': 'GLASS.card',
  'glass-button': 'GLASS.button',
  'glass-button-active': 'GLASS.buttonActive',
  'glass-tabs': 'GLASS.tabs',
  'admin-mesh-bg': 'ADMIN.meshBg',
  'glass-input': 'GLASS.input',
  'glass-shimmer': 'GLASS.shimmer',
  'glass-glow': 'GLASS.glow',
  'glass-stat': 'GLASS.stat',
  'glass-nav': 'GLASS.nav',
  root: 'LAYOUT.root',
  'hit-area-extend': 'INTERACTION.hitAreaExtend',
  'doctor-detail-page': 'PAGE.doctorDetail',
  loading: 'STATE.loading',
  'scrollbar-hide': 'SCROLLBAR.hide',
  'cls-prevent': 'CLS.prevent',
  'card-consistent-height': 'CARD.consistentHeight',
  'image-aspect-ratio': 'IMAGE.aspectRatio',
  'text-stable': 'TEXT.stable',
  'reserve-space': 'LAYOUT.reserveSpace',
  'text-no-reflow': 'TEXT.noReflow',

  // Additional CSS utility classes
  'card-grid': 'LAYOUT.cardGrid',
  quill: 'BLOG_EDITOR.quill',
  'ql-container': 'BLOG_EDITOR.qlContainer',
  'ql-editor': 'BLOG_EDITOR.qlEditor',
  rtl: 'LAYOUT.rtl',

  // Print-related CSS classes
  com: 'DOMAIN.com',
  'line-clamp-2': 'TEXT.lineClamp2',
  'line-clamp-3': 'TEXT.lineClamp3',
  card: 'LAYOUT.card',
  'treatment-card': 'TREATMENT.card',
  'doctor-card': 'DOCTOR.card',
  'page-break-before': 'PRINT.pageBreakBefore',
  'page-break-after': 'PRINT.pageBreakAfter',
  'no-page-break': 'PRINT.noPageBreak',
  'preview-mode-banner': 'PREVIEW.banner',

  // Blog content classes
  'blog-content': 'BLOG.content',

  // Test utility classes
  'base-class': 'TEST.baseClass',
  'active-class': 'TEST.activeClass',
  'disabled-class': 'TEST.disabledClass',
  class1: 'TEST.class1',
  class2: 'TEST.class2',

  // Additional spacing and layout
  'mx-3.5': 'MARGIN_X["3.5"]',
  'px-2.5': 'PADDING_X["2.5"]',
  'py-1.5': 'PADDING_Y["1.5"]',
  'h-px': 'SIZE_SCALE.hPx',

  // Transform utilities
  'translate-x-px': 'TRANSFORM.translateXPx',

  // Touch actions
  'touch-none': 'INTERACTION.touchNone',

  // Group modifiers
  'group/menu-item': 'GROUP.menuItem',

  // Arbitrary values
  'max-w-[600px]': 'SIZE_SCALE.maxW600px',

  // Border colors
  'bg-border': 'COLOR_SCALE.border',
};

// Custom classes mapping - these are design system custom classes
export const CUSTOM_CLASSES_MAP = {
  'no-image-text': 'CUSTOM_CLASSES["no-image-text"]',
  'hero-pattern-layer': 'CUSTOM_CLASSES["hero-pattern-layer"]',
  'provider-dot': 'CUSTOM_CLASSES["provider-dot"]',
  'provider-dot-circle': 'CUSTOM_CLASSES["provider-dot-circle"]',
  'provider-dot-tooltip': 'CUSTOM_CLASSES["provider-dot-tooltip"]',
  'provider-dot-thumbnail': 'CUSTOM_CLASSES["provider-dot-thumbnail"]',
  'provider-dot-featured': 'CUSTOM_CLASSES["provider-dot-featured"]',
  'toc-container': 'CUSTOM_CLASSES["toc-container"]',
  'descendant-text-white-opacity-90': 'CUSTOM_CLASSES["descendant-text-white-opacity-90"]',
  'shimmer-effect': 'CUSTOM_CLASSES["shimmer-effect"]',
};

/**
 * Convert a single Tailwind class to a design token
 * @param {string} tailwindClass - The Tailwind class to convert
 * @returns {string|null} - The design token equivalent, or null if no mapping exists
 */
export function convertClassToToken(tailwindClass: string): string | null {
  // Check all mapping objects
  const allMaps = [
    SPACING_MAP,
    TYPOGRAPHY_MAP,
    COLOR_MAP,
    SIZE_MAP,
    SHADOW_MAP,
    RADIUS_MAP,
    LAYOUT_MAP,
    TEXT_MAP,
    BORDER_MAP,
    ANIMATION_MAP,
    GRID_MAP,
    RESPONSIVE_MAP,
    UTILITY_MAP,
    CUSTOM_CLASSES_MAP,
  ];

  for (const map of allMaps) {
    if (Object.prototype.hasOwnProperty.call(map, tailwindClass)) {
      const token = (map as Record<string, string>)[tailwindClass];
      if (token) {
        return token;
      }
    }
  }

  return null;
}

/**
 * Convert multiple Tailwind classes to design tokens
 * @param {string} classString - Space-separated Tailwind classes
 * @returns {Object} - Object with original classes and their token equivalents
 */
export function convertClassesToTokens(classString: string): {
  original: string;
  converted: string[];
  unmapped: string[];
} {
  const classes = classString.split(/\s+/).filter(Boolean);
  const result: {
    original: string;
    converted: string[];
    unmapped: string[];
  } = {
    original: classString,
    converted: [],
    unmapped: [],
  };

  for (const cls of classes) {
    const token = convertClassToToken(cls);
    if (token) {
      result.converted.push(`${cls} → ${token}`);
    } else {
      result.unmapped.push(cls);
    }
  }

  return result;
}

/**
 * Convert a string of Tailwind classes to design tokens
 * @param {string} classString - Space-separated Tailwind classes
 * @returns {string} - Space-separated design tokens
 */
export function convertClassNameToTokens(classString: string): string {
  const classes = classString.split(/\s+/).filter(Boolean);
  const convertedTokens: string[] = [];

  for (const cls of classes) {
    const token = convertClassToToken(cls);
    if (token) {
      convertedTokens.push(token);
    } else {
      // If no mapping exists, keep the original class
      convertedTokens.push(cls);
    }
  }

  return convertedTokens.join(' ');
}

/**
 * Get a complete mapping of all available conversions
 * @returns {Object} - All mapping objects combined
 */
export function getAllMappings(): {
  spacing: typeof SPACING_MAP;
  typography: typeof TYPOGRAPHY_MAP;
  colors: typeof COLOR_MAP;
  sizes: typeof SIZE_MAP;
  shadows: typeof SHADOW_MAP;
  radius: typeof RADIUS_MAP;
  layout: typeof LAYOUT_MAP;
  text: typeof TEXT_MAP;
  borders: typeof BORDER_MAP;
  animations: typeof ANIMATION_MAP;
  grid: typeof GRID_MAP;
  responsive: typeof RESPONSIVE_MAP;
  utilities: typeof UTILITY_MAP;
  customClasses: typeof CUSTOM_CLASSES_MAP;
} {
  return {
    spacing: SPACING_MAP,
    typography: TYPOGRAPHY_MAP,
    colors: COLOR_MAP,
    sizes: SIZE_MAP,
    shadows: SHADOW_MAP,
    radius: RADIUS_MAP,
    layout: LAYOUT_MAP,
    text: TEXT_MAP,
    borders: BORDER_MAP,
    animations: ANIMATION_MAP,
    grid: GRID_MAP,
    responsive: RESPONSIVE_MAP,
    utilities: UTILITY_MAP,
    customClasses: CUSTOM_CLASSES_MAP,
  };
}
